import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  style: {"background-color":"rgba(var(--ion-color-danger-rgb), 0.8)","color":"var(--ion-color-danger-contrast)","padding":"12px 16px","text-align":"center","font-weight":"500","position":"fixed","top":"0","left":"0","right":"0","z-index":"1000","pointer-events":"none"}
}

import {
  IonApp,
  IonRouterOutlet,
  IonButton,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
} from '@ionic/vue';

// some of the vue router capabilities are not in ionic router.
import {
  useApiStore,
  useCustomerStore,
  useFormStore,
  useSitterStore,
} from './store';
import { close } from 'ionicons/icons';
/* mp035 import capacitor app for hooks */
import { App as CapacitorApp } from '@capacitor/app';
import {
  registerPushNotifications,
  addPushNotificationReceivedListener,
} from './lib/push-notifications';
import MyModal from './components/MyModal.vue';

import { useAdminStore } from './store/admin';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { PushNotificationSchema } from '@capacitor/push-notifications';
import { PluginListenerHandle } from '@capacitor/core';
import { CapacitorUpdater, BundleInfo } from '@capgo/capacitor-updater';
import { apiRequest } from './lib/api-request';
import { statusOk } from './lib/http-request';
import { CapacitorUpdaterController } from './lib/api-routes';
import { useRouter } from 'vue-router';
import { isDevelopmentVersion } from './lib/is-development-version';

// allow cypress tests to access the stores

export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

if (window.Cypress) {
  (window as any).apiStore = useApiStore();
  (window as any).customerStore = useCustomerStore();
  (window as any).sitterStore = useSitterStore();
  (window as any).adminStore = useAdminStore();
  (window as any).formStore = useFormStore();
}

const showPushNotificationModal = ref(false);
const displayedNotification = ref<PushNotificationSchema>();
let pushNotificationsListener: PluginListenerHandle | undefined;
let appStateListener: PluginListenerHandle;
let appUrlOpenListener: PluginListenerHandle | undefined;
const apiStore = useApiStore();
const router = useRouter();

let bundleInfo: BundleInfo | undefined;

/**
 * Handle deep links from App Links (Android) and Universal Links (iOS)
 * Supports both https://app.coastalbabysitters.com.au/... and coastalbabysitters://... schemes
 */
function handleDeepLink(url: string) {
  try {
    const urlObj = new URL(url);
    let path = urlObj.pathname;

    // Remove leading slash if present
    if (path.startsWith('/')) {
      path = path.substring(1);
    }

    // If path is empty, redirect to login
    if (!path || path === '') {
      router.push({ name: 'login' });
      return;
    }

    // Try to match the path to a route
    // The router will handle the routing, including query params and hash
    router.push(urlObj.pathname + urlObj.search + urlObj.hash).catch((err) => {
      // If route doesn't exist, redirect to login
      console.warn('Deep link route not found, redirecting to login:', err);
      router.push({ name: 'login' });
    });
  } catch (error) {
    console.error('Error parsing deep link URL:', error);
    // Fallback to login page if URL parsing fails
    router.push({ name: 'login' });
  }
}

async function checkForUpdate() {
  // check for an available update
  const updateResult = await apiRequest(
    CapacitorUpdaterController.checkForUpdate,
  );
  if (
    statusOk(updateResult) &&
    updateResult.body?.data?.available &&
    updateResult.body?.data?.url &&
    updateResult.body?.data?.version &&
    !bundleInfo
  ) {
    apiStore.bundleDownloading = true;
    try {
      bundleInfo = await CapacitorUpdater.download({
        url: updateResult.body?.data?.url,
        version: updateResult.body?.data?.version,
      });
    } finally {
      apiStore.bundleDownloading = false;
    }
  }

  // if we are on the login page, then apply the update
  // immediately.
  if (bundleInfo && router.currentRoute.value.name === 'login') {
    await CapacitorUpdater.set(bundleInfo);
  }
}

onMounted(async () => {
  // call registerPushNotifications when the app first starts,
  await registerPushNotifications(true);
  pushNotificationsListener = await addPushNotificationReceivedListener(
    (notification) => {
      displayedNotification.value = notification;
      showPushNotificationModal.value = true;
    },
  );

  // hit the update available endpoint.  If it answers
  // without error, call app ready
  const updateResult = await apiRequest(
    CapacitorUpdaterController.checkForUpdate,
  );
  if (statusOk(updateResult)) {
    CapacitorUpdater.notifyAppReady();
  }

  // now run the update check on startup
  await checkForUpdate();

  // then add listeners to register push notificaitons and check
  // for updates when the app is opened.
  appStateListener = await CapacitorApp.addListener(
    'appStateChange',
    async (state) => {
      if (state.isActive) {
        // call register again everytime the app is opened to
        // update the push token
        await registerPushNotifications(false);

        await checkForUpdate();
      } else if (bundleInfo) {
        // if the app is not active, and we have a bundle info,
        // then apply the update immediately.
        await CapacitorUpdater.set(bundleInfo);
      }
    },
  );

  // Handle deep links (App Links for Android, Universal Links for iOS)
  appUrlOpenListener = await CapacitorApp.addListener('appUrlOpen', (data) => {
    handleDeepLink(data.url);
  });

  // Also check if the app was opened with a URL (for cold starts)
  const appUrl = await CapacitorApp.getLaunchUrl();
  if (appUrl?.url) {
    handleDeepLink(appUrl.url);
  }
});

onBeforeUnmount(() => {
  // technically not required, with the current app layout
  // because this component should never be unmounted,
  // but it's good practice to remove listeners
  pushNotificationsListener?.remove();
  appStateListener?.remove();
  appUrlOpenListener?.remove();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonApp), null, {
    default: _withCtx(() => [
      (_unref(isDevelopmentVersion)())
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, " Development Version "))
        : _createCommentVNode("", true),
      _createVNode(_unref(IonRouterOutlet)),
      _createVNode(MyModal, {
        modelValue: showPushNotificationModal.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showPushNotificationModal).value = $event)),
        style: {"background-color":"white"}
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(IonGrid), null, {
            default: _withCtx(() => [
              _createVNode(_unref(IonRow), null, {
                default: _withCtx(() => [
                  _createVNode(_unref(IonCol), {
                    style: {"display":"flex","align-items":"center"},
                    class: "ion-padding-start"
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("h4", null, _toDisplayString(displayedNotification.value?.title), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(IonCol), {
                    size: "auto",
                    style: {"display":"flex","align-items":"center"}
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(IonButton), {
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (showPushNotificationModal.value = false)),
                        fill: "clear"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(IonIcon), {
                            slot: "icon-only",
                            icon: _unref(close)
                          }, null, 8, ["icon"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_unref(IonRow), { class: "ion-padding-horizontal ion-padding-bottom" }, {
                default: _withCtx(() => [
                  _createVNode(_unref(IonCol), null, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(displayedNotification.value?.body), 1)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _: 1
  }))
}
}

})